import React from 'react';
import Box from '@ntuctech/devex-tangram/Box';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';
import SvgInfo from '@ntuctech/devex-tangram/Icon/SvgInfo';
import Modal from '@ntuctech/devex-tangram/Modal';
import ModalActions from '@ntuctech/devex-tangram/ModalActions';
import ModalContent from '@ntuctech/devex-tangram/ModalContent';
import NotificationBanner from '@ntuctech/devex-tangram/NotificationBanner';
import TooltipDialog from '@ntuctech/devex-tangram/Tooltip/TooltipDialog';
import Typography from '@ntuctech/devex-tangram/Typography';
import styled from 'styled-components';

import { getCardType } from '../../services/cards';
import PageTitle from '../PageTitle';

const AccountSc = styled(Modal)`
  border-radius: 0.25rem;
  padding: 2.5rem 3.5rem;
  width: 30rem;

  .instruction-text {
    color: #4d4d4d;
    margin-bottom: 1.5rem;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-evenly;
  border: 0.0625rem solid #eaeaea;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1.25rem;

  /* adjust the height of Grid item */
  & ._ndsGridCont--compact > div {
    height: 1.75rem;
    display: flex;
    align-items: center;
  }

  & .textOverflowControl {
    overflow: hidden;
  }

  & .textOverflowControl > * {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .card-type {
    margin-bottom: 0.5rem;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    padding: 1.25rem;
  }
`;

const CardDetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.25rem 0;
`;

const InfoHeaderSc = styled(Typography)`
  color: #666666;
`;

const NoCardName = styled(Typography)`
  color: #999999;
  display: flex;
  align-items: center;

  .name-tooltip-icon {
    margin-left: 0.5rem;
  }
`;

const LinkCardFace = styled.div`
  min-height: 5.5rem;
  min-width: 8.75rem;
  background-image: url(${(props) => props.cardFaceLink});
  background-repeat: no-repeat;
  background-size: 100%;
  background-position: center;
  border-radius: 0.5rem;

  @media (max-width: 767px) {
    min-height: 6.25rem;
    min-width: 10rem;
    margin: 0 auto;
    margin-bottom: 1.25rem;
  }
`;

const CardAccount = (props) => {
  const { card, cardFaceLink, openCardAccModal, handleOpenCardAcc, handleOpenAddressForm } = props;
  const parseCardNum = React.useMemo(() => card.cardNumber.match(/.{1,4}/g), [card]);

  const handleOnClickNext = () => {
    handleOpenCardAcc(false);
    handleOpenAddressForm(true);
  };

  return (
    <AccountSc open={openCardAccModal}>
      <PageTitle boxMb={2} data-testid="replace_card__title">
        Replace card
      </PageTitle>
      <ModalContent>
        <Typography
          variant="body2"
          className="instruction-text"
          data-testid="replace_card__instructions"
        >
          Check your current card information.
        </Typography>
        <InfoContainer>
          <LinkCardFace cardFaceLink={cardFaceLink} />
          <Box ml={2}>
            <Typography variant="h6" className="card-type">
              {getCardType(card.cardType)}
            </Typography>
            <CardDetailColumn>
              <InfoHeaderSc variant="body3" data-testid="name__title">
                Name
              </InfoHeaderSc>
              {card.nameOnCard === 'null' ? (
                <NoCardName variant="body3" data-testid="name__value" title={card.nameOnCard}>
                  Not available
                  <TooltipDialog
                    invertedBg
                    content={
                      <Typography>
                        We will use your Linkpass registered name on the new card.
                      </Typography>
                    }
                    placement="top"
                  >
                    <SvgInfo size="s" className="name-tooltip-icon" />
                  </TooltipDialog>
                </NoCardName>
              ) : (
                <Typography variant="subhead1" data-testid="name__value" title={card.nameOnCard}>
                  {card.nameOnCard}
                </Typography>
              )}
            </CardDetailColumn>
            <CardDetailColumn>
              <InfoHeaderSc variant="body3" data-testid="card_number__title">
                Card number
              </InfoHeaderSc>
              <Typography variant="subhead1" data-testid="card_number__value">
                {parseCardNum[0]} &nbsp;
                {parseCardNum[1]} &nbsp;
                {parseCardNum[2]} &nbsp;
                {parseCardNum[3]}
              </Typography>
            </CardDetailColumn>
          </Box>
        </InfoContainer>
      </ModalContent>
      <NotificationBanner
        text="Replacing your card will disable your current card. A new card with new numbers will be sent to you."
        icon={<SvgInfo />}
      />
      <ModalActions>
        <ButtonContained
          color="grey"
          onClick={() => handleOpenCardAcc(false)}
          data-testid="cancel_button"
        >
          Cancel
        </ButtonContained>
        <ButtonContained data-testid="next_button" onClick={handleOnClickNext}>
          Next
        </ButtonContained>
      </ModalActions>
    </AccountSc>
  );
};

export default CardAccount;
